import styled, { CSSObject } from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import {
  ASAP_ORDER,
  FulfillmentWindow,
  OrderType,
  PREORDER_ORDER
} from 'shop/types'
import CheckoutFormTitle from 'shop/components/Checkout/CheckoutFormTitle'
import GenericBreakdownItem from 'shop/components/GenericBreakdownItem/GenericBreakdownItem'
import NoteBreakdownItem from '../CartBreakdown/NoteBreakdownItem'
import { IoTimeOutline as ClockIcon } from 'react-icons/io5'
import { format } from 'date-fns'
import { parseTimezoneISO } from 'shop/utils'
import { Medium } from 'shop/components/common'
import { BaseAddress, Fulfillment } from 'shop/types/cart'
import isEmpty from 'lodash/isEmpty'
import { getFulfillmentFlags } from 'shop/utils'
import { formatFulfillmentWindow, keysHaveValues } from 'shop/utils/common'
import ExpandableDiv from 'shop/components/ExpandableContainer/ExpandableDiv'
import { LineBreak } from 'shop/components/Shop/commonStyles'
import FulfillmentBreakdownItem from './FulfillmentBreakdownItem'
import { keys } from 'lodash'

interface Props {
  fulfillment: Fulfillment
  deliveryAddress: BaseAddress | null
  storeAddress: BaseAddress
  dropoffNotes: string | null
  hideAddressSection?: boolean
  onChangeButtonClick?: () => void
  animationsReady?: boolean
}

const FulfillmentDetails = ({
  fulfillment,
  deliveryAddress,
  storeAddress,
  dropoffNotes,
  hideAddressSection = false,
  onChangeButtonClick,
  animationsReady = false
}: Props) => {
  const { orderType, type: fulfillmentType, window } = fulfillment

  const { isPickup, isOrderAtTable, isDelivery } =
    getFulfillmentFlags(fulfillmentType)

  if (
    isPickup &&
    (isEmpty(storeAddress) || !keysHaveValues(keys(storeAddress), storeAddress))
  ) {
    return <></>
  }

  const fulfillmentTitle = {
    DELIVERY: 'Delivery Details',
    PICKUP: 'Pickup Details',
    ORDER_AT_TABLE: 'Table Order Details'
  }

  const createFulfillmentInfo = (
    orderType: OrderType,
    window: FulfillmentWindow
  ) => {
    const dayOfWeek = (date: string) => format(parseTimezoneISO(date), 'EEEE')
    const formattedDate = (date: string) =>
      format(parseTimezoneISO(date), 'dd/MM/yy')
    const isAsap = orderType === ASAP_ORDER
    const isPreorder = orderType === PREORDER_ORDER
    const asapText = isAsap ? ' (ASAP)' : ''
    const todayOrDate = isPreorder
      ? `${dayOfWeek(window.to)} ${formattedDate(window.to)}`
      : 'Today'
    const arrivalOrPickup = isDelivery ? 'Estimated arrival' : 'Pickup time'

    if (isDelivery || isPickup) {
      return (
        <CollapsedDetailsContainer>
          <Medium>{arrivalOrPickup}</Medium>
          <span>
            {`${todayOrDate}, at ${formatFulfillmentWindow(window)}${asapText}`}
          </span>
        </CollapsedDetailsContainer>
      )
    }

    return null
  }

  return (
    <div>
      <CheckoutFormTitle title={fulfillmentTitle[fulfillmentType]} />
      <BreakdownItemContainer>
        {!isOrderAtTable && (
          <>
            <GenericBreakdownItem
              LeftIcon={ClockIcon}
              label={createFulfillmentInfo(orderType, window)}
              padding={'0'}
              withEllipsis={false}
              withLineBreak={false}
              maxWidth={'90%'}
            />
            <LineBreak />
          </>
        )}
        <ExpandableDiv
          isExpanded={!hideAddressSection || !isDelivery}
          collapsedHeight={0}
          transition={animationsReady ? undefined : 'none'}
        >
          <FulfillmentBreakdown
            {...{
              fulfillment,
              deliveryAddress,
              storeAddress,
              onChangeButtonClick,
              dropoffNotes,
              isDelivery
            }}
          />
        </ExpandableDiv>
      </BreakdownItemContainer>
    </div>
  )
}

export const FulfillmentBreakdown = ({
  fulfillment,
  deliveryAddress,
  storeAddress,
  onChangeButtonClick,
  dropoffNotes,
  isDelivery
}: { isDelivery: boolean } & Props) => {
  return (
    <div>
      <FulfillmentBreakdownItem
        fulfillment={fulfillment}
        deliveryAddress={deliveryAddress}
        storeAddress={storeAddress}
        onChangeButtonClick={onChangeButtonClick}
      />
      {isDelivery && !!dropoffNotes && (
        <DeliveryInstructionsContainer>
          <NoteBreakdownItem
            noteTitle={'Delivery instructions'}
            note={dropoffNotes}
          />
        </DeliveryInstructionsContainer>
      )}
    </div>
  )
}

export const Label = styled.p<StyledHTMLElement>(
  (): CSSObject => ({
    margin: 0
  })
)

const DeliveryInstructionsContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    borderRadius: '12px',
    margin: '16px 0'
  })
)

const BreakdownItemContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  })
)

const CollapsedDetailsContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',

    '& i': {
      fontSize: '12px'
    }
  })
)

export default FulfillmentDetails
